import { JWebPromise, WhenJWebReady } from '@jarvis/jweb-core';
import { Button, ButtonGroup, Checkbox, IconInfo, ProgressIndicator } from '@veneer/core';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import HeaderBar from '../../components/HeaderBar';
import ModalCheckoutCancel from '../../components/Modals/CheckoutCancel';
import ModalCheckoutCardExpPayment from '../../components/Modals/CheckoutCardExp';
import ModalCheckoutContinue from '../../components/Modals/CheckoutContinue';
import ModalCheckoutPayment from '../../components/Modals/CheckoutPayment';
import ModalCheckoutShipping from '../../components/Modals/CheckoutShipping';
import ModalError, { ModalErrorDetail } from '../../components/Modals/Error';
import Preview from '../../components/Preview';
import InputsInfoBar from '../../components/InputsInfoBar';
import { errorCodes } from '../../helpers/errorCodes';
import { getProjectExpirationText, validateBillingExpiration } from '../../helpers/expirationDate';
import { ScreenInfo, useDataCollection } from '../../jarvis/dataCollection';
import { sendFinishedEvent } from '../../jarvis/eventService';
import './index.css';

import type { AxiosResponse } from 'axios';
import type { CheckoutInfoData, CheckoutPSFData, CreateOrderData, JarvisWebHttpError, Project } from '../../types';
import { usePhotobookApiWebHttpClient } from '../../helpers/useJarvisApiWebHttpClient';
import { PhotobookApi } from '../../helpers/apis/photobookApi';
import { LocalStorageHelper } from '../../helpers/localStorage';
import { WindowLocationHelper } from '../../helpers/windowLocation';

const CheckoutPage = () => {
  const [jweb, setJWeb] = useState<JWebPromise>();
  const [isLoading, setIsLoading] = useState(true);
  const [checkoutData, setCheckoutInfoData] = useState({} as CheckoutInfoData);
  const [projectData, setProjectData] = useState({} as Project);
  const [activeProjectId, setActiveProjectId] = useState('');
  const [activeProductCategory, setActiveProductCategory] = useState('');
  const [activeUserDevice, setActiveUserDevice] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [hasBillingProcess, setHasBillingProcess] = useState(true);
  const [hasProjectLocalPhotos, setProjectHasLocalPhotos] = useState(false);
  const [isTermsOfServiceRead, setIsTermsOfServiceRead] = useState(false);
  const [isPaymentCardValid, setIsPaymentCardValid] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorDetail, setErrorDetail] = useState<ModalErrorDetail>();
  const [userMediaclipToken, setUserMediaClipToken] = useState('');
  const [isTester, setIsTester] = useState(false);
  const [testFulfiller, setTestFulfiller] = useState(false);
  const [ecommerceEnabled, setEcommerceEnabled] = useState(false);
  const dataCollection = useDataCollection();

  const [showShippingModal, setShowShippingModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentCardExpModal, setShowPaymentCardExpModal] = useState(false);
  const [showOrderConfirmationModal, setShowOrderConfirmationModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const history = useHistory();

  const backButtonName = 'BackButton';
  const continueButtonName = 'ConfirmUploadButton';
  const cancelRequestButtonName = 'CancelRequestButton';
  const cancelConfirmButtonName = 'CancelConfirmButton';
  const shippingInfoIconName = 'ShippingInfoIcon';
  const paymentInfoIconName = 'PaymentInfoIcon';

  const photobookApi = new PhotobookApi(usePhotobookApiWebHttpClient());

  const screenInfo: ScreenInfo = { name: 'Order', activity: 'OrderPhotobook-v01' };

  useEffect(() => {
    LocalStorageHelper.setLastPage('checkout');
    WhenJWebReady.then((_res: JWebPromise) => {
      console.log('CheckoutPage::WhenJWebReady: Ready!');
      setJWeb(_res);
      const device = LocalStorageHelper.getDevice();
      setActiveUserDevice(device ?? 'android');
      const expiresAt = LocalStorageHelper.getExpiresAt();
      setExpirationDate(getProjectExpirationText(expiresAt));
      obtainPsfCheckoutInfo();
    });
    dataCollection.sendScreenViewEvent(screenInfo);
  }, [dataCollection]);

  const handleError = (error: unknown, context: string) => {
    console.error('CheckoutPage::handleError: %o: %o', context, error);
    let errorMessage = '';
    if ((error as JarvisWebHttpError).response?.data) {
      const errorCode = (error as JarvisWebHttpError).response.data.errorCode;
      errorMessage = errorCodes[errorCode];
    }
    setErrorDetail({
      context: context,
      ...(error instanceof Error && { error: error }),
      errorMessage: errorMessage,
    });
    setHasError(true);
    setIsLoading(false);
  };

  const obtainPsfCheckoutInfo = async () => {
    try {
      console.log('CheckoutPage::obtainPSFCheckoutURL: START');
      setIsLoading(true);
      const response: AxiosResponse = await photobookApi.getPsfCheckoutInfo();
      console.log('CheckoutPage::obtainPSFCheckoutURL: SUCCESS: %o', response.data);
      handlePsfData(response.data);
    } catch (error) {
      console.error('CheckoutPage::obtainPSFCheckoutURL: FAILURE: %o', error);
      console.log('PSF not configured for this user, proceding to default v2 checkout');
      obtainCheckoutInfo();
    }
  };

  const obtainCheckoutInfo = async () => {
    try {
      console.log('CheckoutPage::obtainCheckoutInfo: START');
      setIsLoading(true);
      const response: AxiosResponse = await photobookApi.getCheckoutInfo();
      console.log('CheckoutPage::obtainCheckoutInfo: SUCCESS: %o', response.data);
      handleProjectData(response.data);
    } catch (error) {
      handleError(error, 'Checkout v2 (default)');
    }
  };

  const handlePsfData = (data: CheckoutPSFData) => {
    try {
      if (!data) throw new Error('Missing data');
      console.log('Redirecting to PSF checkout URL:', data.url);
      LocalStorageHelper.setLastPage('psf');
      LocalStorageHelper.setPsfUrl(data.url);

      WindowLocationHelper.href(data.url);
    } catch (error) {
      handleError(error, 'Checkout v3 (PSF)');
    }
  };

  const handleProjectData = (data: CheckoutInfoData) => {
    try {
      if (!data) throw new Error('Missing data');
      const {
        projects,
        billing,
        billingAddress,
        userMediaclipToken: returnedUserMediaclipToken,
        isTester: returnedIsTester,
        testFulfiller: returnedTestFulfiller,
        ecommerceEnabled: returnedEcommerceEnabled,
      } = data;
      if (!projects[0]) {
        throw new Error('Missing project');
      }
      const { id: returnedProjectId, productCategory = 'photobook', hasLocalPhotos } = projects[0];
      if (projects[0].price === '$0.00' || projects[0].price === '$0,00') {
        projects[0].price = 'FREE';
        setHasBillingProcess(false);
      }
      if (!billing) {
        console.error('CheckoutPage::handleProjectData: Missing billing data from the API response');
        setHasBillingProcess(false);
      } else {
        const isValidExpiration = validateBillingExpiration(billing.creditCard?.cardExpirationDate);
        setIsPaymentCardValid(isValidExpiration);
      }
      if (!billingAddress) {
        console.error('CheckoutPage::handleProjectData: Missing billingAddress data from the API response');
        setHasBillingProcess(false);
      }
      setCheckoutInfoData(data);
      setProjectData(projects[0]);
      setActiveProjectId(returnedProjectId);
      setUserMediaClipToken(returnedUserMediaclipToken);
      setActiveProductCategory(productCategory.toLowerCase());
      setIsTester(returnedIsTester ? true : false);
      setTestFulfiller(returnedTestFulfiller ? true : false);
      setEcommerceEnabled(returnedEcommerceEnabled ? true : false);
      setProjectHasLocalPhotos(hasLocalPhotos);
      setIsLoading(false);
    } catch (error) {
      handleError(error, 'Checkout v2 (default)');
    }
  };

  const onClickBack = () => {
    sendClickEvent(backButtonName);
    history.push('/cart');
  };

  // Send Analytics
  const sendClickedEvent = (controlName: string) => {
    dataCollection.sendControlClickedEvent({ name: controlName, detail: activeProjectId }, screenInfo);
  };

  const sendClickEvent = (controlName: string) => {
    dataCollection.sendControlClickedEvent({ name: controlName, detail: activeProjectId }, screenInfo);
  };

  // Shipping modal's confirm / cancel button handlers
  const onOpenShippingInfoTapped = () => {
    sendClickedEvent(shippingInfoIconName);
    setShowShippingModal(true);
  };

  const onCloseShippingInfoTapped = () => {
    setShowShippingModal(false);
  };

  // Payment modal's confirm / cancel button handlers
  const onOpenPaymentInfoTapped = () => {
    sendClickedEvent(paymentInfoIconName);
    setShowPaymentModal(true);
  };

  const onClosePaymentInfoTapped = () => {
    setShowPaymentModal(false);
  };

  const onClosePaymentCardExpTapped = () => {
    setShowPaymentCardExpModal(false);
  };

  // Order modal's confirm / cancel button handlers
  const onConfirmOrderTapped = async () => {
    sendClickedEvent(continueButtonName);
    if (!isPaymentCardValid) {
      return setShowPaymentCardExpModal(true);
    }
    if (isTermsOfServiceRead) {
      submitOrder();
    }
  };

  const submitOrder = useCallback(() => {
    const successStatusCodes = [200, 201];
    const timeoutStatusCodes = [503, 504];

    // Submit the order
    const handleSubmitOrder = async () => {
      try {
        console.log('CheckoutPage::handleSubmitOrder: START');
        setHasError(false);
        setIsLoading(true);
        const response = await photobookApi.createOrder(successStatusCodes.concat(timeoutStatusCodes));

        // After a checkout, our photobook flow is considered complete.
        // The user should be starting over at the beginning if relaunched.
        LocalStorageHelper.clearProject();

        if (timeoutStatusCodes.includes(response.status)) {
          console.error('CheckoutPage::handleSubmitOrder: TIMEOUT (%d)', response.status);
          setErrorDetail({
            context: 'Order Timeout',
            errorTitle: 'Order Timed Out',
            errorMessage: 'Order processing timed out. Please try again. ',
            retryCallback: submitOrder,
          });
          setHasError(true);
          setIsLoading(false);
          return;
        }

        console.log('CheckoutPage::handleSubmitOrder: SUCCESS (%d): %o', response.status, response.data);

        if (jweb?.Plugins.MCDesigner != undefined) {
          const { links } = response.data as CreateOrderData;
          const orderResponse = {
            cancelUrl: links.order.cancelUrl,
            releaseUrl: links.order.releaseUrl,
            photoUploadUrl: links.photo.uploadUrl,
            photoStatusUrl: links.photo.statusUrl,
          };
          jweb?.Plugins.MCDesigner.onOrderSubmitted(orderResponse)
            .then(() => {
              console.log('CheckoutPage::handleSubmitOrder::MCDesigner: Native app notified');
              setIsLoading(false);
            })
            .catch(function (error) {
              console.error('CheckoutPage::handleSubmitOrder::MCDesigner: %o', error);
              setErrorDetail({
                context: 'Error notifying native about order',
                ...(error instanceof Error && { error: error }),
              });
              setHasError(true);
              setIsLoading(false);
            });
        } else {
          setIsLoading(false);
          console.log('CheckoutPage::handleSubmitOrder::MCDesigner: No plugin detected');
        }
      } catch (error) {
        handleError(error, 'Unable to submit order');
      }
    };

    handleSubmitOrder().then(() => setShowOrderConfirmationModal(true));
  }, [photobookApi, jweb?.Plugins.MCDesigner]);

  const onOrderConfirmedModalOkayTapped = () => {
    window.open('https://survey.medallia.com/?photobook-survey-1', '_blank');
    sendFinishedEvent();
  };

  const onOrderConfirmedModalCloseTapped = () => {
    setShowOrderConfirmationModal(false);
    sendFinishedEvent();
  };

  // Cancel modal's confirm / cancel button handlers
  const onCancelBackTapped = () => {
    sendClickedEvent(cancelRequestButtonName);
    setShowCancelModal(true);
  };

  const onCancelConfirmTapped = () => {
    sendClickedEvent(cancelConfirmButtonName);
    sendFinishedEvent();
  };

  // Handle Terms of Service Checkbox
  const onTermsOfServiceClick = (event: React.FormEvent<HTMLInputElement>) => {
    setIsTermsOfServiceRead(event.currentTarget.checked);
  };

  const categoryBannerTitle: {
    [index: string]: JSX.Element;
  } = {
    photobook: <h1 className="title">Enjoy your HP Photobook!</h1>,
    canvas: <h1 className="title">Enjoy your HP Canvas!</h1>,
  };

  const deviceNextSteps: {
    [index: string]: {
      [index: string]: JSX.Element;
    };
  } = {
    local: {
      photobook: (
        <>
          <p>
            Your photobook is processing: Don’t delete your photos yet. Images are uploading. Image upload might take a
            few minutes, and requires a network connection.
          </p>
          <p>After image upload completes, your photobook will arrive in about two weeks</p>
        </>
      ),
      canvas: (
        <>
          <p>
            Your canvas is processing: Don’t delete your photos yet. Images are uploading. Image upload might take a few
            minutes, and requires a network connection.
          </p>
          <p>After image upload completes, your canvas will arrive in about two weeks</p>
        </>
      ),
    },
    remote: {
      photobook: <p>Your photobook is processing and will arrive in approximately two weeks.</p>,
      canvas: <p>Your canvas will be processed and arrive in approximately two weeks.</p>,
    },
  };

  if (hasError) {
    return <ModalError state={hasError} detail={errorDetail as ModalErrorDetail} screenInfo={screenInfo} />;
  }

  if (isLoading) {
    return (
      <div className="spinner">
        <ProgressIndicator />
      </div>
    );
  }

  return (
    <>
      <HeaderBar handleBack={onClickBack} title="Checkout" />
      <section className="CheckoutPage">
        <div className="banner">
          {categoryBannerTitle[activeProductCategory]}
          <div className="subTitle">
            Thanks for being an Instant Ink customer and one of the first 250 participants in HP Memories. Confirm your
            information below to complete your order.
          </div>
        </div>
        <div className={`imageContainer ${activeProductCategory}`}>
          <Preview
            projectId={activeProjectId}
            userMediaclipToken={userMediaclipToken}
            category={activeProductCategory}
          />
        </div>
        <div className="checkoutInfo">
          <div className="cart">
            <div className="cartItem">
              <div className="itemName">
                <span className="title">{projectData.productFriendlyName}</span>
                <span className="price">{projectData.price}</span>
              </div>
            </div>
          </div>
          <div className="totalPriceInfo">
            <div className="priceItem total">
              <span className="name">
                <b>Subtotal</b>
              </span>
              <span className="value">
                <b>{projectData.price}</b>
                {hasBillingProcess && ' + tax*'}
              </span>
            </div>
            {hasBillingProcess && (
              <div className="taxDesc">
                <p>* Tax included in the charge total. See confirmation email for details</p>
              </div>
            )}
          </div>
          <div className="additionalInfo">
            <div className="shipping">
              <div className="sectionTitle">
                <span className="title">Shipped To</span>
                <span className="icon" onClick={onOpenShippingInfoTapped}>
                  <IconInfo size={20} filled color="colorHpBlue6" />
                </span>
              </div>
              <div className="address">
                <p>{checkoutData.address?.fullName}</p>
                <p>{checkoutData.address?.street1}</p>
                {checkoutData.address?.street2 && <p>{checkoutData.address?.street2}</p>}
                <p>
                  {checkoutData.address?.city}, {checkoutData.address?.state} {checkoutData.address?.zipCode}
                </p>
              </div>
            </div>
            {hasBillingProcess && (
              <div className="payment">
                <div className="sectionTitle">
                  <span className="title">Payment</span>
                  <span className="icon" onClick={onOpenPaymentInfoTapped}>
                    <IconInfo size={20} filled color="colorHpBlue6" />
                  </span>
                </div>
                {checkoutData.billing?.paymentType === 'credit_card' && (
                  <>
                    <p className="billingDescriptionRow">
                      <span>{checkoutData.billing?.creditCard?.cardTypeKey?.toUpperCase()}</span>
                      {checkoutData.billing?.creditCard?.cardNumber}
                    </p>
                    <p className="billingDescriptionRow">
                      <span>Expires</span>
                      {checkoutData.billing?.creditCard?.cardExpirationDate}
                    </p>
                  </>
                )}
                {checkoutData.billing?.paymentType === 'pay_pal' && (
                  <>
                    <p className="billingDescriptionRow">PayPal</p>
                    <p className="billingDescriptionRow">{checkoutData.billing?.payPal?.email}</p>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="nextStepsInfo">
            <div className="sectionTitle">
              <span className="title">Next Steps</span>
            </div>
            {hasProjectLocalPhotos
              ? deviceNextSteps['local'][activeProductCategory]
              : deviceNextSteps['remote'][activeProductCategory]}
          </div>
        </div>
        <div className="termsOfService">
          <Checkbox className="checkBox" checked={isTermsOfServiceRead} onChange={onTermsOfServiceClick} />
          <p>
            I have read and agree to the{' '}
            <a
              href="https://static-dev.tropos-rnd.com/jarvis/react-photobook/latest/terms-of-service.html"
              target="_blank"
              rel="noreferrer"
            >
              HP Terms of Service
            </a>
          </p>
        </div>
        <div className="checkoutButtons">
          <ButtonGroup orientation="horizontal">
            <Button appearance="secondary" onClick={onCancelBackTapped}>
              Exit
            </Button>
            <Button onClick={onConfirmOrderTapped} disabled={!isTermsOfServiceRead}>
              Confirm Order
            </Button>
          </ButtonGroup>
          {isTester && (
            <div className="debug-disclaimer caption-small">
              <p>* You are configured as a Tester.</p>
              {testFulfiller ? <p>order will not print</p> : <p>ORDER WILL PRINT</p>}
              {ecommerceEnabled ? <p>YOUR CARD WILL BE CHARGED</p> : <p>your card will not be charged</p>}
            </div>
          )}
        </div>
        <ModalCheckoutShipping
          onCloseShippingInfoTapped={onCloseShippingInfoTapped}
          showShippingModal={showShippingModal}
        />
        <ModalCheckoutPayment onClosePaymentInfoTapped={onClosePaymentInfoTapped} showPaymentModal={showPaymentModal} />
        <ModalCheckoutCardExpPayment
          onClosePaymentCardExpTapped={onClosePaymentCardExpTapped}
          showPaymentCardExpModal={showPaymentCardExpModal}
        />
        <ModalCheckoutContinue
          showOrderConfirmationModal={showOrderConfirmationModal}
          onOrderConfirmedModalOkayTapped={onOrderConfirmedModalOkayTapped}
          onOrderConfirmedModalCloseTapped={onOrderConfirmedModalCloseTapped}
          category={activeProductCategory}
        />
        <ModalCheckoutCancel
          showCancelModal={showCancelModal}
          onCancelCloseTapped={setShowCancelModal}
          onCancelBackTapped={setShowCancelModal}
          onCancelConfirmTapped={onCancelConfirmTapped}
          device={activeUserDevice}
          category={activeProductCategory}
          expirationDate={expirationDate}
        />
      </section>
      {isTester && <InputsInfoBar />}
    </>
  );
};

export default CheckoutPage;
